import { Download, Share, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const DISMISSED_KEY = 'doorly:pwa-install-dismissed';
const DISMISSAL_TTL_MS = 7 * 24 * 3600 * 1000;

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  const navAny = navigator as NavigatorWithStandalone;
  if (navAny.standalone === true) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function isDismissed(): boolean {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < DISMISSAL_TTL_MS;
}

// Capture Chrome/Edge's beforeinstallprompt event globally
let deferredPrompt: BeforeInstallPromptEvent | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
}

export async function triggerInstall(): Promise<boolean> {
  if (deferredPrompt) {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    return outcome === 'accepted';
  }
  return false;
}

export function canInstallNatively(): boolean {
  return deferredPrompt !== null;
}

// Banner that shows on ALL platforms when not installed as PWA
export function InstallPWAPrompt() {
  const [visible, setVisible] = useState(false);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissed()) return;

    const checkPrompt = () => setHasNativePrompt(deferredPrompt !== null);
    checkPrompt();
    const id = setTimeout(() => {
      checkPrompt();
      setVisible(true);
    }, 2000);

    window.addEventListener('beforeinstallprompt', () => {
      setHasNativePrompt(true);
      setVisible(true);
    });

    return () => clearTimeout(id);
  }, []);

  if (!visible) return null;

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  }

  async function handleInstall() {
    if (hasNativePrompt) {
      const accepted = await triggerInstall();
      if (accepted) setVisible(false);
    }
  }

  return (
    <div className="fixed bottom-20 md:bottom-4 inset-x-3 md:inset-x-auto md:right-4 md:max-w-sm z-40">
      <div
        className="bg-ink text-white shadow-xl p-4 flex items-start gap-3"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-body">Installer Doorly</div>
          {isIOS() ? (
            <p className="text-white/70 text-meta mt-1 leading-relaxed">
              Appuie sur{' '}
              <Share className="inline h-3 w-3 -mt-0.5" />{' '}
              <span className="font-semibold text-white">Partager</span> puis{' '}
              <span className="font-semibold text-white">Sur l'écran d'accueil</span> pour
              travailler hors-ligne.
            </p>
          ) : hasNativePrompt ? (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleInstall}
                className="px-3 py-1.5 rounded-lg bg-white text-ink text-meta font-bold"
              >
                Installer maintenant
              </button>
            </div>
          ) : (
            <p className="text-white/70 text-meta mt-1 leading-relaxed">
              Depuis le menu de ton navigateur, choisis{' '}
              <span className="font-semibold text-white">Installer l'application</span> pour
              un accès rapide et le mode hors-ligne.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 p-1 -m-1 text-white/40 hover:text-white"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Sidebar item for permanent access to install instructions
export function InstallAppButton() {
  const [standalone, setStandalone] = useState(true);

  useEffect(() => {
    setStandalone(isStandalone());
  }, []);

  if (standalone) return null;

  async function handleClick() {
    if (deferredPrompt) {
      await triggerInstall();
    } else {
      // Reset dismiss timer so the banner reappears
      localStorage.removeItem(DISMISSED_KEY);
      window.location.reload();
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-body text-ink-mute hover:bg-surface-alt cursor-pointer"
    >
      <Download className="h-4 w-4" />
      Installer l'app
    </button>
  );
}
